"use client";

import React from "react";
import { motion } from 'framer-motion';
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { fadeInVariants, cardVariants } from "@/lib/animations";
import DeleteAccountForm from "@/components/customer/DeleteAccountForm";
import type { PageUrlMap } from "@/types/blog";

interface CustomerSettingsProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

export default function CustomerSettings({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerSettingsProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerSettings',
    { language, translations, pageUrls },
    isBackendRender
  );

  const t = useCustomerPanelTranslations(translations);

  return (
    <motion.div 
      {...backendAttributes}
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div 
        className="mb-8"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="text-3xl font-bold text-gray-900">
          {t('customer_panel.settings.title', 'Settings')}
        </h1>
      </motion.div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {/* Language Settings (Placeholder) */}
        <motion.div 
          className="bg-white rounded-lg drop-shadow-card border border-gray-200 p-6 group hover:shadow-2xl transition-all duration-300"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('customer_panel.settings.language', 'Language')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('customer_panel.settings.language_description', 'Language settings will be available soon.')}
          </p>
        </motion.div>

        {/* Theme Settings (Placeholder) */}
        <motion.div 
          className="bg-white rounded-lg drop-shadow-card border border-gray-200 p-6 group hover:shadow-2xl transition-all duration-300"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('customer_panel.settings.theme', 'Theme')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('customer_panel.settings.theme_description', 'Theme settings will be available soon.')}
          </p>
        </motion.div>

        {/* Delete Account Section */}
        <motion.div 
          className="bg-white rounded-lg drop-shadow-card border border-red-200 p-6 group hover:shadow-2xl transition-all duration-300"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <h2 className="text-lg font-semibold text-red-900 mb-4">
            {t('customer_panel.settings.delete_account', 'Delete Account')}
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            {t('customer_panel.settings.delete_account_warning', 'Are you sure you want to delete your account? This action cannot be undone.')}
          </p>
          <DeleteAccountForm 
            language={language} 
            translations={translations}
            pageUrls={pageUrls}
            defaultLanguage={defaultLanguage}
          />
        </motion.div>
      </div>
    </motion.div>
  );
}

